import { call, cancel, select, takeEvery } from 'redux-saga/effects';
import {
  DruckRequestPayload,
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  Gefahr,
  getBeamtenKennung,
  getBeruflicheStellung,
  mapLeistungen,
  mapToBffVorvertragsdatenDruck,
  mapZusatzoptionenDruck,
  numberToGermanNumberFormattedString,
  Produktlinie,
  VersicherterBereichEnum,
} from '@ps20-frontend/frontend-models';
import {
  FahrzeugDruck,
  Feature,
  RsDruckdaten,
  SpartenType,
  TaasTarifrisikoschluesselRS,
  VersicherterEinzelbereich,
  VersicherterEinzelbereichDruck,
  VorschaedenDruck,
  ZusatzoptionenDruck,
} from '@ps20/bff-models';
import { PS20Logger } from '@ps20-frontend/logging';
import {
  getProduktlinienDruck,
  mapLinienpreis,
  mapVermieteteObjekte,
} from '@ps20-frontend/druckdaten-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import {
  selectSelectedProduktlinie,
  selectZusatzoptionItems,
} from '../../app/shared/selectors/produkt.selector';
import { IAppState, Rechtsstreitigkeiten } from '../appState';
import { selectVermieteteObjekte } from '../../app/shared/selectors/vermieteteObjekte.selector';

export function* watchDruckdatenRequested() {
  yield takeEvery(GlobalActions.DRUCKDATEN_REQUESTED, safeSaga(onDruckdatenRequested, App.RS));
}

export function* onDruckdatenRequested(action: GlobalAction<DruckRequestPayload>) {
  try {
    const appIds: string[] = action.payload.appIds;

    if (!appIds.includes(App.RS)) {
      yield cancel();
      return;
    }

    const state: IAppState = yield select();
    const globalPut = GlobalPut.getInstance();

    const rsDruckdaten: RsDruckdaten = yield call(mapDruckdaten, state);
    yield call([globalPut, globalPut.put], new GlobalActions().druckdatenGenerated(rsDruckdaten));
  } catch (e) {
    PS20Logger.error(App.RS, e as Error, 'Druckdatensaga.onDruckdatenRequested()');
    yield cancel();
  }
}

export function mapDruckdaten(state: IAppState): RsDruckdaten {
  const zusatzoptionenDruck: Array<ZusatzoptionenDruck> = mapZusatzoptionenDruck(
    selectZusatzoptionItems(state),
  );
  const ausgewaehlteProduktlinie = selectSelectedProduktlinie(state);
  const isKombiVertrag =
    VersicherterBereichEnum.KOMBI === state.vertragsoptionen.versicherterBereich;
  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);
  const druckdaten: RsDruckdaten = {
    antragsnummer: state.antragsnummer,
    longText: ausgewaehlteProduktlinie.langtext,
    shortText: ausgewaehlteProduktlinie.kurztext,
    rbdAktuell: state.taasInfo.rbdNachlass,
    treuebonus: state.taasInfo.tbNachlass,
    vorteilsRabatt: vorteilsRabattActivated ? state.taasInfo.vorteilsrabatt || 0 : undefined,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    zaehlstuecke: state.rabattstufe,
    spartenType: SpartenType.RECHTSSCHUTZ,
    beginn: null, // kommt vom Checkout
    ablauf: null, // kommt vom Checkout
    gesamtbeitrag: numberToGermanNumberFormattedString(
      mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
        state.vertragsoptionen.zahlungsweise,
        state.gesamtbeitragMitRabatt,
        state.jahresbeitrag,
        App.RS,
      ),
    ),
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    vertragsdauer: null,
    linienpreis: mapLinienpreis(state.produkt),
    leistungen: mapLeistungen(ausgewaehlteProduktlinie, SpartenType.RECHTSSCHUTZ),
    zusatzoptionenDruck,
    vorschaedenDruck: mapVorschaeden(state.rechtsstreitigkeiten),
    vorversicherungenDruck: null,
    produktlinien: getProduktlinienDruck(state.produkt),
    zuersGefaehrdungsklasse: '',
    isKombi: isKombiVertrag,
    beamtenKennung: getBeamtenKennung(state.imOeffentlichenDienstTaetig),
    beruflicheStellung: getBeruflicheStellung(state.selbststaendigkeit),
    geschaeftsfuehrer: state.selbststaendigkeit.geschaeftsfuehrer,
    familienstand: state.versicherungsnehmer.familienstand,
    vorvertragsnummern: mapToBffVorvertragsdatenDruck(state.vertragsoptionen.vorvertragsNummern),
    vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
    abweichungVomZielmarkt: false,
    wartezeitverzichtDruck: null,
    beibehalten: state.vertragsoptionen.beibehalten,
    nachlass: ausgewaehlteProduktlinie.nachlass,
    kooperation: state.vertragsoptionen.selectedKooperation,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
  };

  if (isKombiVertrag) {
    druckdaten.versicherteEinzelbereiche = mapVersicherteEinzelbereiche(state);

    const vermieteteObjekte = selectVermieteteObjekte(state);
    if (vermieteteObjekte != null && vermieteteObjekte.length > 0) {
      druckdaten.vermieteteObjekte = mapVermieteteObjekte(vermieteteObjekte);
    }
  } else {
    druckdaten.fahrzeugDruck = mapFahrzeugDruck(state, ausgewaehlteProduktlinie);
  }

  return druckdaten;
}

function mapVorschaeden(rechtsstreitigkeiten: Rechtsstreitigkeiten): VorschaedenDruck {
  return {
    rechtsstreit: rechtsstreitigkeiten.anzahl,
  };
}

function mapVersicherteEinzelbereiche(state: IAppState): Array<VersicherterEinzelbereichDruck> {
  return selectSelectedProduktlinie(state).gefahren.map(gefahr => {
    return {
      versicherterEinzelbereich: convertToVersicherterEinzelbereich(gefahr),
      taasTarifrisikoschluessel: gefahr.name,
      isSelected: gefahr.isSelected,
      plzFaktor: gefahr.plzFaktor,
      anzahlVorschaeden: gefahr.anzahlVorschaeden,
      deltafaktor: gefahr.deltafaktor,
    };
  });
}

export function convertToVersicherterEinzelbereich(gefahr: Gefahr): VersicherterEinzelbereich {
  if (TaasTarifrisikoschluesselRS.PRIVAT === gefahr.name) {
    return VersicherterEinzelbereich.PRIVAT;
  } else if (TaasTarifrisikoschluesselRS.BERUF === gefahr.name) {
    return VersicherterEinzelbereich.BERUF;
  } else if (TaasTarifrisikoschluesselRS.VERKEHR === gefahr.name) {
    return VersicherterEinzelbereich.VERKEHR;
  } else if (TaasTarifrisikoschluesselRS.WOHNEINHEIT === gefahr.name) {
    return VersicherterEinzelbereich.WOHNEN;
  }
}

function mapFahrzeugDruck(state: IAppState, selectedPl: Produktlinie): FahrzeugDruck {
  return {
    amtlichesKennzeichen: null, // kommt vom Checkout
    fahrzeugeArt: state.vertragsoptionen.fahrzeug.fahrzeugArt,
    fahrzeugeVersichert: state.vertragsoptionen.fahrzeug.anzahlFahrzeug,
    deltafaktor: selectedPl.gefahren.find(value => value.name === 'VERKEHR')?.deltafaktor,
    plzFaktor: selectedPl.gefahren.find(value => value.name === 'VERKEHR')?.plzFaktor,
    anzahlVorschaeden: selectedPl.gefahren.find(value => value.name === 'VERKEHR')
      ?.anzahlVorschaeden,
  };
}
